// The bar's outer buttons: not the game's ends, but the previous and the
// next of the reader's mistakes (blunders and misses, the review API's
// `momentsIn`), and the start or the end only when no mistake lies that way
// (2026-10-13; before, the outer buttons were first and last). Pure, tested.
import { momentLabel, momentsIn, type Moment, type ReviewState } from '@chess/api-review';

export interface OuterStep {
  /** Where the button goes: a mistake's ply (the position after the move), or 0 or the last ply. */
  ply: number;
  kind: 'mistake' | 'edge';
  /** Blunder or miss, for the tone, when the stop is a mistake. */
  mistake?: Moment['kind'];
  label: string;
}

export interface OuterSteps {
  back: OuterStep;
  forward: OuterStep;
}

const WORD: Record<Moment['kind'], string> = { blunder: 'blunder', miss: 'miss' };

/**
 * The two stops for a position of the game (the board's ply, or the ply
 * looked back at): the last mistake before it and the first after it, the
 * game's start and end when there is none. Undefined without a game.
 */
export function outerSteps(state: ReviewState): OuterSteps | undefined {
  const game = state.game;
  if (state.status !== 'ready' || !game) return undefined;
  const total = game.moves.length;
  const at = state.browse ?? state.ply;
  const moments = momentsIn(state.scan, game.moves, 0, total, state.reader);
  const before = [...moments].reverse().find((m) => m.ply < at);
  const after = moments.find((m) => m.ply > at);
  return {
    back: before
      ? { ply: before.ply, kind: 'mistake', mistake: before.kind, label: `Previous ${WORD[before.kind]}: ${momentLabel(before)}` }
      : { ply: 0, kind: 'edge', label: 'Go to the starting position' },
    forward: after
      ? { ply: after.ply, kind: 'mistake', mistake: after.kind, label: `Next ${WORD[after.kind]}: ${momentLabel(after)}` }
      : { ply: total, kind: 'edge', label: 'Go to the final position' },
  };
}
